import { motion } from "framer-motion";
import { Textarea } from "@/components/ui/textarea";
import { Lightbulb, Sparkles } from "lucide-react";

export const InputContextField = ({ 
  value, 
  onChange, 
  inputType = "url",
  placeholder = ""
}) => {
  // Dynamic placeholders based on input type
  const placeholders = {
    url: "e.g., Create promotional posts like this website for my travel agency...",
    image: "e.g., Use this product photo to create an exciting launch announcement...",
    voice: "e.g., Turn this voice note into engaging social media content...",
  };
  
  // Dynamic helper text
  const helperTexts = {
    url: [
      "What kind of posts do you want? (promotional, educational, etc.)",
      "What should we focus on from this URL?",
      "Any specific style or tone you're looking for?",
    ],
    image: [
      "What story should this image tell?",
      "Who is your target audience?",
      "What action do you want viewers to take?",
    ],
    voice: [
      "What's the main message you want to convey?",
      "Should we highlight any specific details?",
    ],
  };
  
  const hints = helperTexts[inputType] || helperTexts.url;
  
  return (
    <motion.div 
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-3 mt-4"
    >
      {/* Enticing header */}
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-viraly-orange/20 to-viraly-green/20 flex items-center justify-center flex-shrink-0">
          <Sparkles className="w-5 h-5 text-viraly-orange" />
        </div>
        <div>
          <h4 className="text-sm font-medium text-viraly-light">
            Add Your Vision <span className="text-viraly-muted font-normal">(Tell AI What You Want)</span>
          </h4>
          <p className="text-xs text-viraly-muted mt-0.5">
            Your instructions are prioritized - the more specific, the better the result
          </p>
        </div>
      </div>
      
      {/* Textarea with gradient border on focus */}
      <div className="relative">
        <Textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder || placeholders[inputType]}
          className="min-h-[100px] bg-white/5 border-white/10 text-viraly-light placeholder:text-viraly-muted/50 focus:border-viraly-orange/50 focus:ring-viraly-orange/20 resize-none"
          data-testid="input-context-field"
        />
        
        {/* Character hint */}
        <div className="absolute bottom-2 right-2 text-[10px] text-viraly-muted/50">
          {value.length > 0 ? `${value.length} chars` : "Optional but magical"}
        </div>
      </div>
      
      {/* Quick hint chips */}
      <div className="flex flex-wrap gap-2">
        <div className="flex items-center gap-1 text-xs text-viraly-muted">
          <Lightbulb className="w-3 h-3" />
          <span>Ideas:</span>
        </div>
        {hints.map((hint, idx) => (
          <button
            key={idx}
            type="button"
            onClick={() => {
              // Append hint to existing text
              const newValue = value 
                ? `${value}\n${hint}` 
                : hint;
              onChange(newValue);
            }}
            className="text-[10px] px-2 py-1 rounded-full bg-white/5 text-viraly-muted hover:bg-viraly-orange/10 hover:text-viraly-orange transition-colors"
          >
            {hint}
          </button>
        ))}
      </div>
      
      {/* Value proposition */}
      {!value && (
        <div className="flex items-center gap-2 p-3 rounded-lg bg-gradient-to-r from-viraly-orange/5 to-transparent border border-viraly-orange/10">
          <span className="text-lg">✨</span>
          <p className="text-xs text-viraly-muted">
            <span className="text-viraly-orange font-medium">Without context</span>, we'll do our best guess. 
            <span className="text-viraly-light"> With context</span>, we'll nail it.
          </p>
        </div>
      )}
    </motion.div>
  );
};
